import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Shield, Plus, Pencil, Trash2, Lock, Users as UsersIcon, Save, X, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { rbacAPI } from '@/services/api';

// Friendly labels for the canonical segments returned by the backend.
const SEGMENT_LABELS = {
  // App features
  chatbots: 'AI Employees',
  knowledge: 'Knowledge Base',
  conversations: 'Conversations & Test Chat',
  leads: 'Leads Pipeline',
  channels: 'Social Channels',
  webhooks: 'Outgoing Webhooks',
  automations: 'Automations & Rules',
  calendar: 'Calendar Integration',
  help_desk: 'Help Desk Integrations',
  audit_logs: 'Audit Logs (User)',
  analytics_user: 'Analytics (User)',
  // Team & workspace
  team: 'Team Members',
  moderators: 'Moderator Seats',
  account_settings: 'Account Settings',
  billing: 'Billing & Subscriptions',
  referrals: 'Refer & Earn',
  // Admin panels
  overview: 'Admin Overview',
  users: 'Users (Admin)',
  plans: 'Plans',
  payments: 'Payments',
  resellers: 'Resellers',
  content: 'Content',
  docs: 'Docs',
  support: 'Support',
  smtp: 'SMTP',
  stripe: 'Stripe',
  social: 'Social Integrations',
  pricing_config: 'Pricing Config',
  analytics: 'Analytics (Admin)',
  branding_seo: 'Branding & SEO',
  locations: 'Locations',
  roles: 'Roles & Permissions',
};

// Fallback groups if the backend doesn't return `groups` (older deployments).
const FALLBACK_GROUPS = [
  {
    id: 'app_features', label: 'App Features',
    description: 'Day-to-day builder features for AI Employees.',
    segments: ['chatbots', 'knowledge', 'conversations', 'leads', 'channels', 'webhooks', 'automations', 'calendar', 'help_desk', 'audit_logs', 'analytics_user'],
  },
  {
    id: 'team_workspace', label: 'Team & Workspace',
    description: 'Teammates, moderators, billing, and account-level settings.',
    segments: ['team', 'moderators', 'account_settings', 'billing', 'referrals'],
  },
  {
    id: 'admin_panels', label: 'Admin Panels',
    description: 'System-admin only surfaces. Grant only to trusted operators.',
    segments: ['overview', 'users', 'plans', 'payments', 'resellers', 'content', 'docs', 'support', 'smtp', 'stripe', 'social', 'pricing_config', 'analytics', 'branding_seo', 'locations', 'roles'],
  },
];

const ACTION_LABELS = {
  read: 'Read',
  write: 'Create',
  edit: 'Edit',
  delete: 'Delete',
};

const emptyPerms = (segments, actions) => {
  const out = {};
  segments.forEach((s) => {
    out[s] = {};
    actions.forEach((a) => { out[s][a] = false; });
  });
  return out;
};

const PermissionMatrix = ({ segments, actions, value, onChange, disabled, groups }) => {
  const toggle = (seg, act) => {
    if (disabled) return;
    const next = { ...value, [seg]: { ...(value[seg] || {}), [act]: !value?.[seg]?.[act] } };
    onChange(next);
  };

  const toggleRow = (seg, target) => {
    if (disabled) return;
    const next = { ...value, [seg]: {} };
    actions.forEach((a) => { next[seg][a] = target; });
    onChange(next);
  };

  const toggleGroup = (segs, target) => {
    if (disabled) return;
    const next = { ...value };
    segs.forEach((seg) => {
      next[seg] = {};
      actions.forEach((a) => { next[seg][a] = target; });
    });
    onChange(next);
  };

  // Prefer server-provided groups; fall back to a single "All permissions"
  // group covering every returned segment so we still render sensibly if
  // the backend returns a legacy flat list.
  const usableGroups = (groups && groups.length)
    ? groups.map((g) => ({ ...g, segments: g.segments.filter((s) => segments.includes(s)) }))
        .filter((g) => g.segments.length)
    : [{ id: 'all', label: 'Permissions', description: '', segments }];

  // Any segments not covered by any group — dump them under an "Other" section
  // so nothing silently disappears if the backend adds a segment before the
  // group list gets updated.
  const grouped = new Set(usableGroups.flatMap((g) => g.segments));
  const orphans = segments.filter((s) => !grouped.has(s));
  if (orphans.length) {
    usableGroups.push({ id: 'other', label: 'Other', description: 'New segments not yet categorized.', segments: orphans });
  }

  return (
    <div className="space-y-4">
      {usableGroups.map((group) => {
        const groupSegs = group.segments;
        const allOn = groupSegs.every((seg) => actions.every((a) => value?.[seg]?.[a]));
        const anyOn = groupSegs.some((seg) => actions.some((a) => value?.[seg]?.[a]));
        return (
          <div key={group.id} className="border rounded-lg overflow-hidden" data-testid={`perm-group-${group.id}`}>
            <div className="flex items-start justify-between gap-3 px-3 py-2 bg-slate-100 border-b">
              <div>
                <div className="text-sm font-semibold text-slate-800">{group.label}</div>
                {group.description && (
                  <div className="text-xs text-slate-500">{group.description}</div>
                )}
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button
                  type="button"
                  size="sm"
                  variant={allOn ? 'default' : 'outline'}
                  onClick={() => toggleGroup(groupSegs, true)}
                  disabled={disabled}
                  className="h-7 text-xs"
                  data-testid={`perm-group-${group.id}-grant-all`}
                >
                  Grant all
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={anyOn ? 'outline' : 'default'}
                  onClick={() => toggleGroup(groupSegs, false)}
                  disabled={disabled}
                  className="h-7 text-xs"
                  data-testid={`perm-group-${group.id}-revoke-all`}
                >
                  Revoke all
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-slate-700">Feature</th>
                    {actions.map((a) => (
                      <th key={a} className="text-center px-3 py-2 font-medium text-slate-700 capitalize">
                        {ACTION_LABELS[a] || a}
                      </th>
                    ))}
                    <th className="text-center px-3 py-2 font-medium text-slate-500">All</th>
                  </tr>
                </thead>
                <tbody>
                  {groupSegs.map((seg) => {
                    const segPerms = value?.[seg] || {};
                    const rowAll = actions.every((a) => segPerms[a]);
                    return (
                      <tr key={seg} className="border-b last:border-b-0 hover:bg-slate-50/60">
                        <td className="px-3 py-2 font-medium text-slate-800">
                          {SEGMENT_LABELS[seg] || seg}
                        </td>
                        {actions.map((a) => (
                          <td key={a} className="text-center px-3 py-2">
                            <Checkbox
                              data-testid={`perm-${seg}-${a}`}
                              checked={!!segPerms[a]}
                              onCheckedChange={() => toggle(seg, a)}
                              disabled={disabled}
                            />
                          </td>
                        ))}
                        <td className="text-center px-3 py-2">
                          <Button
                            type="button"
                            size="sm"
                            variant={rowAll ? 'default' : 'outline'}
                            onClick={() => toggleRow(seg, !rowAll)}
                            disabled={disabled}
                            className="h-7 text-xs"
                            data-testid={`perm-${seg}-all`}
                          >
                            {rowAll ? 'All' : 'None'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const AdminRolesTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [segments, setSegments] = useState([]);
  const [actions, setActions] = useState(['read', 'write', 'edit', 'delete']);
  const [groups, setGroups] = useState(FALLBACK_GROUPS);

  const [editingRole, setEditingRole] = useState(null); // role being edited or null
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', permissions: {} });

  const load = async () => {
    try {
      setLoading(true);
      const [segRes, rolesRes] = await Promise.all([
        rbacAPI.getSegments(),
        rbacAPI.listRoles(),
      ]);
      setSegments(segRes.data.segments);
      setActions(segRes.data.actions);
      if (Array.isArray(segRes.data.groups) && segRes.data.groups.length) {
        setGroups(segRes.data.groups);
      }
      setRoles(rolesRes.data.roles);
    } catch (err) {
      console.error('Failed to load roles', err);
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingRole(null);
    setForm({
      name: '',
      description: '',
      permissions: emptyPerms(segments, actions),
    });
    setShowDialog(true);
  };

  const openEdit = (role) => {
    setEditingRole(role);
    setForm({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || emptyPerms(segments, actions),
    });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error('Role name is required');
      return;
    }
    setSaving(true);
    try {
      if (editingRole) {
        await rbacAPI.updateRole(editingRole.role_id, {
          name: form.name,
          description: form.description,
          permissions: form.permissions,
        });
        toast.success('Role updated');
      } else {
        await rbacAPI.createRole({
          name: form.name,
          description: form.description,
          permissions: form.permissions,
        });
        toast.success('Role created');
      }
      setShowDialog(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (role) => {
    // Only the system_admin role is undeletable — all other built-in roles
    // (support, moderator, etc.) can now be deleted. Users on a deleted role
    // get reassigned to the default "user" role by the backend.
    if (role.role_id === 'system_admin') {
      toast.error('The system_admin role cannot be deleted.');
      return;
    }
    if (!window.confirm(`Delete role "${role.name}"? Users on this role will be moved to "User".`)) return;
    try {
      await rbacAPI.deleteRole(role.role_id);
      toast.success('Role deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete role');
    }
  };

  const duplicate = async (role) => {
    const newName = window.prompt(`Duplicate "${role.name}" as:`, `${role.name} (copy)`);
    if (!newName || !newName.trim()) return;
    try {
      await rbacAPI.duplicateRole(role.role_id, { new_name: newName.trim() });
      toast.success(`Role duplicated as "${newName}"`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to duplicate role');
    }
  };

  const grantedCount = useMemo(() => {
    return roles.map((r) => {
      let count = 0;
      Object.values(r.permissions || {}).forEach((acts) => {
        Object.values(acts).forEach((v) => { if (v) count += 1; });
      });
      return count;
    });
  }, [roles]);

  if (loading) {
    return <div className="text-slate-500 py-10 text-center">Loading roles…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-emerald-600" />
            Roles & Permissions
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Create custom roles (moderator, sub-admin, etc.) and define which admin segments they can access.
          </p>
        </div>
        <Button data-testid="rbac-create-role-btn" onClick={openCreate} className="gap-2">
          <Plus className="w-4 h-4" /> New Role
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((role, idx) => (
          <Card key={role.role_id} className="border-slate-200" data-testid={`rbac-role-card-${role.role_id}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    {role.name}
                    {role.is_built_in && (
                      <Badge variant="outline" className="text-xs gap-1">
                        <Lock className="w-3 h-3" /> Built-in
                      </Badge>
                    )}
                    {grantedCount[idx] === 0 && role.role_id !== 'role_system_admin' && role.role_id !== 'system_admin' && (
                      <Badge className="text-xs bg-amber-100 text-amber-800 border border-amber-200" title="This role grants no permissions — users on it behave identically to the default User role.">
                        No permissions
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="text-xs mt-1 line-clamp-2">
                    {role.description || 'No description'}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex items-center justify-between text-xs text-slate-600 mb-3">
                <span className="flex items-center gap-1">
                  <UsersIcon className="w-3 h-3" /> {role.user_count} user{role.user_count === 1 ? '' : 's'}
                </span>
                <span>{grantedCount[idx]} permission{grantedCount[idx] === 1 ? '' : 's'}</span>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEdit(role)}
                  className="flex-1 gap-1"
                  data-testid={`rbac-edit-${role.role_id}`}
                >
                  <Pencil className="w-3 h-3" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => duplicate(role)}
                  className="gap-1"
                  data-testid={`rbac-duplicate-${role.role_id}`}
                  title="Duplicate this role as a new one"
                >
                  <Copy className="w-3 h-3" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => remove(role)}
                  disabled={role.role_id === 'system_admin'}
                  className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`rbac-delete-${role.role_id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="rbac-role-dialog">
          <DialogHeader>
            <DialogTitle>{editingRole ? `Edit Role — ${editingRole.name}` : 'Create New Role'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="role-name">Role Name *</Label>
                <Input
                  id="role-name"
                  data-testid="rbac-role-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Sub Admin, Moderator"
                />
              </div>
              <div>
                <Label htmlFor="role-desc">Description</Label>
                <Input
                  id="role-desc"
                  data-testid="rbac-role-description-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What can this role do?"
                />
              </div>
            </div>

            <div>
              <Label className="mb-2 block">
                Permissions
                {editingRole?.role_id === 'system_admin' && (
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    (locked — system_admin must retain full access)
                  </span>
                )}
              </Label>
              <PermissionMatrix
                segments={segments}
                actions={actions}
                groups={groups}
                value={form.permissions}
                onChange={(p) => setForm({ ...form, permissions: p })}
                disabled={editingRole?.role_id === 'system_admin'}
              />
              <p className="text-xs text-slate-500 mt-2">
                Read = view; Create = add new; Edit = modify existing; Delete = remove items.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="gap-1">
              <X className="w-4 h-4" /> Cancel
            </Button>
            <Button
              data-testid="rbac-save-role-btn"
              onClick={save}
              disabled={saving}
              className="gap-1"
            >
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : (editingRole ? 'Save Changes' : 'Create Role')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Reusable matrix exported so the Users tab can render the override editor.
export { PermissionMatrix, SEGMENT_LABELS, ACTION_LABELS, emptyPerms };
